import { Button } from 'react-bootstrap';
import type { TFunction } from 'i18next';
import type { TppEventSummary } from '../../../services/transitPassPool.service';
import { displayTppPurpose } from '../../../utils/tppPurposeDisplay';
import {
  historyColumnLabelKey,
  TPP_HISTORY_SORTABLE_COLUMNS,
  type TppHistoryColumnId,
} from '../../../utils/tppHistoryTableLayout';
import TppProofUploadButton from './TppProofUploadButton';
import { tppActivateKeyHandler } from './tppUiShared';
import {
  displayTppEventKind,
  formatHistoryAmount,
  formatHistoryDate,
  tppEventNeedsProofUi,
} from './tppHistoryFormat';

type SortHelpers = {
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (col: TppHistoryColumnId) => void;
  sortIndicator: (col: TppHistoryColumnId) => string | null;
};

export function renderTppHistoryHeaderCell(
  col: TppHistoryColumnId,
  t: TFunction,
  { sortBy, sortDir, onSort, sortIndicator }: SortHelpers
) {
  const sortable = TPP_HISTORY_SORTABLE_COLUMNS.includes(col);
  const label = t(historyColumnLabelKey(col));
  const alignEnd = col === 'amount' ? ' text-end' : '';
  if (!sortable) {
    return (
      <th key={col} className={alignEnd || undefined}>
        {label}
      </th>
    );
  }
  const activate = () => onSort(col);
  const ariaSort =
    sortBy === col ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th
      key={col}
      scope="col"
      tabIndex={0}
      aria-sort={ariaSort}
      className={`user-select-none${alignEnd}`}
      onClick={activate}
      onKeyDown={tppActivateKeyHandler(activate)}
      style={{ cursor: 'pointer' }}
    >
      {label}
      {sortIndicator(col)}
    </th>
  );
}

type BodyCellCtx = {
  t: TFunction;
  useJaDates: boolean;
  amountPrefix: string;
  amountSuffix: string;
  formatAbsAmount: (n: number) => string;
  onEditEvent: (ev: TppEventSummary) => void;
  onUploadProof: (eventId: number, file: File) => void;
  uploadProofPending: boolean;
  uploadProofEventId?: number;
  uploadProofError: boolean;
  onViewProof: (eventId: number) => void;
  proofViewerLoading: boolean;
};

/** 該当しない／空の履歴セル（チャージ行の「—」だらけを避ける） */
function blankCell(col: TppHistoryColumnId) {
  return <td key={col} className="align-middle" />;
}

export function renderTppHistoryBodyCell(
  col: TppHistoryColumnId,
  ev: TppEventSummary,
  ctx: BodyCellCtx
) {
  const {
    t,
    useJaDates,
    amountPrefix,
    amountSuffix,
    formatAbsAmount,
    onEditEvent,
    onUploadProof,
    uploadProofPending,
    uploadProofEventId,
    uploadProofError,
    onViewProof,
    proofViewerLoading,
  } = ctx;
  const isCharge = ev.eventKind === 'charge';

  switch (col) {
    case 'id':
      return <td key={col}>{ev.id}</td>;
    case 'eventKind':
      return <td key={col}>{displayTppEventKind(ev, t)}</td>;
    case 'outDate':
      return <td key={col} className="text-nowrap">{formatHistoryDate(ev.outDate, useJaDates)}</td>;
    case 'inDate':
      if (isCharge) return blankCell(col);
      if (!ev.inDate) {
        return (
          <td key={col} className="text-nowrap text-warning-emphasis">
            {t('tpp.history_not_returned')}
          </td>
        );
      }
      return <td key={col} className="text-nowrap">{formatHistoryDate(ev.inDate, useJaDates)}</td>;
    case 'borrowerName': {
      const name = ev.borrowerName?.trim();
      return name ? <td key={col}>{name}</td> : blankCell(col);
    }
    case 'purpose': {
      const purpose = displayTppPurpose(ev.eventKind, ev.purpose, t);
      return purpose ? (
        <td key={col} className="text-truncate" style={{ maxWidth: 180 }} title={purpose}>
          {purpose}
        </td>
      ) : (
        blankCell(col)
      );
    }
    case 'route': {
      if (isCharge) return blankCell(col);
      const route = ev.route?.trim();
      return route ? (
        <td key={col} className="small text-break" style={{ maxWidth: 280 }} title={route}>
          {route}
        </td>
      ) : (
        blankCell(col)
      );
    }
    case 'amount': {
      const amt = formatHistoryAmount(ev, amountPrefix, amountSuffix, formatAbsAmount);
      return (
        <td key={col} className={`text-nowrap text-end ${amt.className}`}>
          {amt.text}
        </td>
      );
    }
    case 'checkoutStatus':
      if (isCharge || ev.checkoutStatus !== 1) return blankCell(col);
      return <td key={col}>{t('tpp.status_out')}</td>;
    case 'proof':
      if (isCharge) return blankCell(col);
      // 未返却かつ証跡なしは返却モーダル。返却済みは後付け可
      if (!tppEventNeedsProofUi(ev)) return blankCell(col);
      return (
        <td key={col} className="align-middle text-nowrap">
          <div className="d-inline-flex align-items-center gap-2 flex-wrap">
            {ev.hasProof ? (
              <Button
                type="button"
                size="sm"
                variant="outline-primary"
                disabled={proofViewerLoading}
                onClick={() => onViewProof(ev.id)}
              >
                {t('tpp.view_proof')}
              </Button>
            ) : null}
            <TppProofUploadButton
              hasProof={ev.hasProof}
              pending={uploadProofPending && uploadProofEventId === ev.id}
              label={t('tpp.upload_proof_short')}
              replaceLabel={t('tpp.replace_proof_short')}
              iconOnly
              onFile={(f) => onUploadProof(ev.id, f)}
            />
            {uploadProofError && uploadProofEventId === ev.id && (
              <span className="text-danger small">{t('tpp.proof_upload_failed')}</span>
            )}
          </div>
        </td>
      );
    case 'edit':
      return (
        <td key={col}>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => onEditEvent(ev)}
            aria-label={t('common.edit')}
            title={t('common.edit')}
          >
            <i className="fas fa-pen" aria-hidden />
          </Button>
        </td>
      );
    default:
      return blankCell(col);
  }
}
